/**
 * User Settings API Hooks
 *
 * TanStack Query hooks for managing user-specific settings.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_BASE, getAuthHeaders } from './client';

// ============================================================================
// Types
// ============================================================================

export interface UserSettings {
    user_id: string;
    use_direct_llm_parsing: boolean;
    created_at: string | null;
    updated_at: string | null;
}

export interface UpdateUserSettingsRequest {
    use_direct_llm_parsing?: boolean;
}

// ============================================================================
// Query Keys
// ============================================================================

export const userSettingsKeys = {
    all: ['user-settings'] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch current user's settings.
 */
export function useUserSettings() {
    return useQuery({
        queryKey: userSettingsKeys.all,
        queryFn: async () => {
            const headers = await getAuthHeaders();
            const res = await fetch(`${API_BASE}/user-settings`, { headers });

            if (!res.ok) {
                throw new Error('Failed to fetch user settings');
            }

            const data = await res.json();
            return data.data as UserSettings;
        },
    });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Update user settings.
 */
export function useUpdateUserSettings() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (request: UpdateUserSettingsRequest) => {
            const headers = await getAuthHeaders();
            const res = await fetch(`${API_BASE}/user-settings`, {
                method: 'PUT',
                headers,
                body: JSON.stringify(request),
            });

            if (!res.ok) {
                const error = await res.json();
                throw new Error(error.error || 'Failed to update user settings');
            }

            const data = await res.json();
            return data.data as UserSettings;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: userSettingsKeys.all });
        },
    });
}
